'use client'

import { useMemo, useState } from 'react'

import { DateRangeInput } from '~/app/accounting/filters/DateRangeInput'
import { FilterPopoverButton } from '~/app/accounting/filters/FilterPopoverButton'
import {
  DEFAULT_PERIOD,
  Period,
  getPeriodLabel,
  getPeriodPresets,
  usePeriodPresets,
} from '~/app/accounting/period-utils'
import { Timestamp } from '~/components/component-library/Timestamp'
import { useAccountingTimezone } from '~/hooks/useAccountingTimezone'
import {
  AccountingTz,
  endOfDay,
  reinterpretYmdAcrossTz,
} from '~/utils/timezone'
import { sendTrackingData } from '~/utils/tracking'

export const DateFilterButton = ({
  period,
  setPeriod,
}: {
  period: Period | null
  setPeriod: (period: Period | null, overrideTz?: AccountingTz) => void
}) => {
  const [tz, setTz] = useAccountingTimezone()
  const [localTz, setLocalTz] = useState(tz)
  const [syncedTz, setSyncedTz] = useState(tz)
  if (syncedTz !== tz) {
    setSyncedTz(tz)
    setLocalTz(tz)
  }

  const [localPeriod, setLocalPeriod] = useState<Period>(
    period ?? DEFAULT_PERIOD,
  )
  const [syncedPeriod, setSyncedPeriod] = useState(period)
  if (syncedPeriod !== period) {
    setSyncedPeriod(period)
    setLocalPeriod(period ?? DEFAULT_PERIOD)
  }
  const groupedPresets = usePeriodPresets(localTz)
  const presets = useMemo(() => groupedPresets.flat(), [groupedPresets])

  const isAppliedEmpty = period == null || period.label === DEFAULT_PERIOD.label
  const isLocalEmpty =
    localPeriod.label === DEFAULT_PERIOD.label &&
    !localPeriod.startDate &&
    !localPeriod.endDate

  const hasChanged = (() => {
    if (localTz !== tz) return true
    const applied = period ?? DEFAULT_PERIOD
    if (localPeriod.startDate?.getTime() !== applied.startDate?.getTime())
      return true
    if (localPeriod.endDate?.getTime() !== applied.endDate?.getTime())
      return true
    return false
  })()

  const isPreset = presets.some((p) => p.label === period?.label)

  const activeLabel = useMemo(() => {
    if (!period) return null
    if (isPreset) return period.label
    return (
      <>
        {period.startDate && (
          <Timestamp
            timestamp={period.startDate.getTime()}
            type="absolute"
            tz={tz}
          />
        )}
        {period.startDate && period.endDate && ' - '}
        {period.endDate && (
          <Timestamp
            timestamp={period.endDate.getTime()}
            type="absolute"
            tz={tz}
          />
        )}
        {!period.startDate && !period.endDate && period.label}
      </>
    )
  }, [period, isPreset, tz])

  const toggleTz = () => {
    const newTz = localTz === 'utc' ? 'local' : 'utc'
    setLocalTz(newTz)
    // Recompute presets in the new tz so "now"-anchored ranges follow the
    // day boundary (PT 5pm Apr 24 ↔ UTC 0:00 Apr 25) instead of freezing.
    const newPresets = getPeriodPresets(newTz).flat()
    const matchedPreset = newPresets.find((p) => p.label === localPeriod.label)
    if (matchedPreset) {
      setLocalPeriod(matchedPreset)
      return
    }
    const newStart = reinterpretYmdAcrossTz(
      localPeriod.startDate,
      localTz,
      newTz,
    )
    const newEndMidnight = reinterpretYmdAcrossTz(
      localPeriod.endDate,
      localTz,
      newTz,
    )
    const rawEnd = newEndMidnight ? endOfDay(newEndMidnight, newTz) : undefined
    const now = new Date()
    const newEnd = rawEnd && rawEnd.getTime() > now.getTime() ? now : rawEnd
    setLocalPeriod({
      label: getPeriodLabel({ startDate: newStart, endDate: newEnd }),
      startDate: newStart,
      endDate: newEnd,
    })
  }

  return (
    <FilterPopoverButton
      label="Date"
      activeLabel={activeLabel}
      isAppliedEmpty={isAppliedEmpty}
      isLocalEmpty={isLocalEmpty}
      hasChanged={hasChanged}
      onApply={() => {
        setTz(localTz)
        setPeriod(localPeriod, localTz)
        sendTrackingData('accountingDatePeriodApplied', {
          periodLabel: localPeriod.label,
          isPreset: presets.some((p) => p.label === localPeriod.label),
          startDate: localPeriod.startDate?.toISOString(),
          endDate: localPeriod.endDate?.toISOString(),
        })
      }}
      onReset={() => {
        setLocalPeriod(DEFAULT_PERIOD)
        setPeriod(DEFAULT_PERIOD)
      }}
      onClear={() => {
        setLocalPeriod(DEFAULT_PERIOD)
        setPeriod(DEFAULT_PERIOD)
      }}
      onClose={() => {
        setLocalPeriod(period ?? DEFAULT_PERIOD)
        setLocalTz(tz)
      }}
    >
      {(focusAction) => (
        <DateRangeInput
          period={localPeriod}
          presets={presets}
          setPeriod={setLocalPeriod}
          tz={localTz}
          onToggleTz={toggleTz}
          onPresetSelect={focusAction}
        />
      )}
    </FilterPopoverButton>
  )
}
